
import { onMounted, reactive, ref } from "vue";
import XAtable from "@/components/XATable.vue";
import XDialog from "@/components/XDialog.vue";
import { message } from "ant-design-vue";
import { roleQueryPageList, roleQueryDetails, roleUpdateMenu, roleAdd } from "@/api/api";

interface SearchFormState {
  roleName: string;
}

interface AddFormState {
  roleName: string;
  roleType: number | undefined;
  status: number | undefined;
}

export default {
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      roleName: "",
    });
    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);

    // 权限配置相关
    const permissionVisible = ref(false);
    const permissionLoading = ref(false);
    const currentRoleId = ref<number | null>(null);
    const checkedKeys = ref<number[]>([]);
    const menuTreeData = ref<any[]>([]);

    // 添加角色相关
    const addVisible = ref(false);
    const addLoading = ref(false);
    const addFormRef = ref();
    const addFormState = reactive<AddFormState>({
      roleName: "",
      roleType: undefined,
      status: undefined,
    });
    const columns = reactive([
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
        width: 80,
      },
      {
        title: "角色名称",
        key: "roleName",
        dataIndex: "roleName",
        width: 150,
      },
      {
        title: "角色类型",
        key: "roleType",
        dataIndex: "roleType",
        width: 120,
        slots: {
          customRender: "roleType",
        },
      },
      {
        title: "状态",
        key: "status",
        dataIndex: "status",
        width: 100,
        slots: {
          customRender: "status",
        },
      },
      //   {
      //     title: "备注",
      //     key: "remark",
      //     dataIndex: "remark",
      //     width: 200,
      //   },
      //   {
      //     title: "开始日期",
      //     key: "beginTime",
      //     dataIndex: "beginTime",
      //     width: 180,
      //   },
      //   {
      //     title: "结束日期",
      //     key: "endTime",
      //     dataIndex: "endTime",
      //     width: 180,
      //   },
      {
        title: "创建时间",
        key: "createTime",
        dataIndex: "createTime",
        width: 180,
      },
      //   {
      //     title: "更新时间",
      //     key: "updateTime",
      //     dataIndex: "updateTime",
      //     width: 180,
      //   },
      {
        title: "操作",
        key: "control",
        dataIndex: "control",
        width: 120,
        fixed: "right",
        slots: {
          customRender: "control",
        },
      },
    ]);
    const tableData = ref([]);

    const search = () => {
      pageNo.value = 1;
      getData();
    };
    const resetForm = () => {
      searchFormState.roleName = "";
      pageNo.value = 1;
      getData();
    };
    const getData = () => {
      loading.value = true;
      const params = {
        roleName: searchFormState.roleName,
        pageNo: pageNo.value,
        pageSize: pageSize.value,
      };
      roleQueryPageList(params).then((res) => {
        loading.value = false;
        if (res.code === "200") {
          total.value = res.data.total || 0;
          tableData.value = res.data.records || [];
        }
      });
    };
    const pageNoChange = (val: number) => {
      pageNo.value = val;
      getData();
    };
    const onShowSizeChange = (current: number, size: number) => {
      pageSize.value = size;
      pageNo.value = 1;
      getData();
    };
    // 配置权限
    const configPermission = async (row: any) => {
      currentRoleId.value = row.id;
      permissionVisible.value = true;

      try {
        // 获取角色权限详情（包含所有菜单和选中状态）
        const detailRes = await roleQueryDetails({ id: row.id });
        if (detailRes.code === "200" && detailRes.data) {
          // 设置菜单树数据
          menuTreeData.value = detailRes.data;
          // 提取 type === 1 的菜单ID（已选中的）
          const selectedIds = extractCheckedMenuIds(detailRes.data);
          checkedKeys.value = selectedIds;
        }
      } catch (error) {
        console.error("获取权限数据失败:", error);
      }
    };

    // 递归提取 type === 1 的菜单ID（只提取叶子节点，避免父节点也被选中导致问题）
    const extractCheckedMenuIds = (menuList: any[]): number[] => {
      const ids: number[] = [];
      menuList.forEach((item: any) => {
        // 如果有子节点，递归处理
        if (item.children && item.children.length > 0) {
          ids.push(...extractCheckedMenuIds(item.children));
        } else {
          // 叶子节点且 type === 1 时添加
          if (item.type === 1) {
            ids.push(item.id);
          }
        }
      });
      return ids;
    };

    // 取消权限配置
    const handlePermissionCancel = () => {
      permissionVisible.value = false;
      permissionLoading.value = false;
      checkedKeys.value = [];
      currentRoleId.value = null;
    };

    // 确认权限配置
    const confirmPermission = async () => {
      if (!currentRoleId.value) {
        message.warning("角色ID不存在");
        return;
      }

      try {
        permissionLoading.value = true;

        const res = await roleUpdateMenu({
          id: currentRoleId.value,
          menuList: checkedKeys.value,
        });

        if (res.code === "200") {
          message.success("权限配置成功");
          permissionVisible.value = false;
          checkedKeys.value = [];
          currentRoleId.value = null;
        } else {
          message.error(res.message || "权限配置失败");
        }
      } catch (error) {
        console.error("更新权限失败:", error);
        message.error("权限配置失败，请重试");
      } finally {
        permissionLoading.value = false;
      }
    };

    // 添加角色
    const addClick = () => {
      addVisible.value = true;
      addFormState.roleName = "";
      addFormState.roleType = undefined;
      addFormState.status = undefined;
    };

    // 取消添加
    const handleAddCancel = () => {
      addVisible.value = false;
      addLoading.value = false;
      addFormRef.value?.resetFields();
    };

    // 确认添加
    const confirmAdd = async () => {
      try {
        await addFormRef.value?.validate();
        addLoading.value = true;

        const res = await roleAdd({
          roleName: addFormState.roleName,
          roleType: addFormState.roleType,
          status: addFormState.status,
        });

        if (res.code === "200") {
          message.success("添加成功");
          addVisible.value = false;
          addFormRef.value?.resetFields();
          getData();
        } else {
          message.error(res.message || "添加失败");
        }
      } catch (error: any) {
        if (error?.errorFields) {
          // 表单验证失败
          return;
        }
        console.error("添加角色失败:", error);
        message.error("添加失败，请重试");
      } finally {
        addLoading.value = false;
      }
    };

    onMounted(() => {
      getData();
    });
    return {
      searchFormState,
      columns,
      tableData,
      pageNo,
      pageSize,
      total,
      loading,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      configPermission,
      permissionVisible,
      permissionLoading,
      checkedKeys,
      menuTreeData,
      handlePermissionCancel,
      confirmPermission,
      addClick,
      addVisible,
      addLoading,
      addFormState,
      addFormRef,
      handleAddCancel,
      confirmAdd,
    };
  },
};
